// AIDEV-NOTE: Pagina para editar pedido em status 'rascunho'
// Carrega pedido existente, popula o store e renderiza o wizard em modo edicao
import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, CircularProgress, Alert, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { Pedido, PedidoItem, AddItemDto } from '@azoupvendas/shared';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { usePedidoStore } from '../stores/pedido.store';
import { PedidoWizard } from '../components/wizard/PedidoWizard';

async function fetchPedido(id: string): Promise<Pedido> {
  const response = await api.get<{ pedido: Pedido }>(`/pedidos/${id}`);
  return response.data.pedido;
}

// AIDEV-NOTE: TODA coluna NUMERIC do Postgres chega como string no JSON ("3.000", "12.3400") —
// o pool nao registra type parser (db.pool.ts). O addItemSchema exige number, entao qualquer
// numerico esquecido aqui derruba o autosave com 400 INVALID_INPUT, que o wizard trata como
// erro definitivo (marcarNaoRetryavel) e trava o indicador em "Erro ao salvar".
function toNumeroOuNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

/** Converte um item vindo de GET /pedidos/:id para o formato AddItemDto do store. */
export function mapPedidoItemParaStore(item: PedidoItem): AddItemDto {
  const bruto = item as unknown as Record<string, unknown>;
  return {
    produto_codigo: Number(item.produto_codigo),
    referencia: item.referencia ?? '',
    descricao: item.descricao,
    cor: item.cor ?? '',
    tamanho: item.tamanho ?? '',
    // AIDEV-NOTE: converter p/ Number tambem evita casas decimais espurias nos campos
    // Qtd/Preco do carrinho ("3.000" -> 3)
    quantidade: Number(item.quantidade),
    valor_unitario: Number(item.valor_unitario),
    desconto_percentual: Number(item.desconto_percentual ?? 0),
    origem_estoque: item.origem_estoque ?? 'proprio',
    // AIDEV-NOTE: Preserva codcor/codtamanho/custo/codbarras do item original para sync correto
    codcor: toNumeroOuNull(bruto.codcor),
    codtamanho: toNumeroOuNull(bruto.codtamanho),
    custo: toNumeroOuNull(bruto.custo),
    codbarras: bruto.codbarras != null ? String(bruto.codbarras) : null
  };
}

export function EditarPedidoPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    setCliente,
    setPedidoId,
    setTabelaPreco,
    setItens,
    resetPedido,
    setDraftOrigem,
    setDraftVendedorId
  } = usePedidoStore();

  const {
    data: pedido,
    isLoading,
    isError,
    error
  } = useQuery<Pedido>({
    queryKey: ['pedido', id],
    queryFn: () => fetchPedido(id!),
    enabled: !!id
  });

  // AIDEV-NOTE: Popula store com dados do pedido existente antes de abrir wizard
  useEffect(() => {
    if (!pedido) return;

    // AIDEV-NOTE: Permite editar rascunho, finalizado e erro_sync que ainda não foram sincronizados (numero null)
    const editavel =
      pedido.status === 'rascunho' ||
      ((pedido.status === 'finalizado' || pedido.status === 'erro_sync') && !pedido.numero);
    if (!editavel) {
      navigate(`/pedidos/${id}`);
      return;
    }

    // AIDEV-NOTE: Se o store ja tem o pedido carregado, nao re-inicializa — evita reset do wizard
    // quando a query é invalidada (ex: após finalização bem-sucedida no Step5).
    if (usePedidoStore.getState().pedidoId === pedido.id) return;

    resetPedido();
    setCliente({
      CODIGO: pedido.cliente_codigo,
      RAZAO_SOCIAL: pedido.cliente_nome,
      CNPJ_CPF: pedido.cliente_cpf_cnpj ?? null,
      CIDADE: '',
      // AIDEV-NOTE: reidrata limite de desconto especial do cliente ao reabrir rascunho para edicao
      // (findById do server agora retorna via LEFT JOIN com politicas_cliente) — sem isso o Step3
      // perdia o limite especial e caia na politica padrao do vendedor.
      limite_desconto_especial: pedido.limite_desconto_especial ?? null
    });
    setPedidoId(pedido.id);
    // AIDEV-NOTE: Finding 1 + 3 (final review) — marca o draft persistido como 'edicao' e registra
    // o vendedor dono. Sem isso, sair desta pagina sem finalizar deixava o store com o pedido em
    // edicao no localStorage; abrir /pedidos/novo depois oferecia "continuar" esse draft (que pode
    // estar finalizado/erro_sync, disparando autosave 400 em loop) ou vazar para outro vendedor em
    // dispositivo compartilhado. Ver podeRecuperarDraft() em pedido.store.ts.
    setDraftOrigem('edicao');
    setDraftVendedorId(user?.vendedor_id ?? null);
    if (pedido.tabela_preco_id) {
      setTabelaPreco(pedido.tabela_preco_id);
    }

    // Converte itens do pedido para AddItemDto
    if (pedido.itens && pedido.itens.length > 0) {
      setItens(pedido.itens.map(mapPedidoItemParaStore));
    }
  }, [
    pedido,
    id,
    navigate,
    resetPedido,
    setCliente,
    setPedidoId,
    setTabelaPreco,
    setItens,
    setDraftOrigem,
    setDraftVendedorId,
    user?.vendedor_id
  ]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !pedido) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Erro ao carregar pedido'}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/pedidos')}>
          Voltar
        </Button>
      </Box>
    );
  }

  return (
    <PedidoWizard
      editMode
      pedidoStatus={pedido.status}
      initialRevision={pedido.revision}
      onCancel={() => navigate(`/pedidos/${id}`)}
      initialValues={{
        condicaoId: pedido.condicao_pagto_id ?? null,
        tipoMovimentoReceberId: pedido.tipo_movimento_receber_id ?? null,
        observacao: pedido.observacao ?? '',
        origem: pedido.origem ?? '',
        descontoGeral: pedido.desconto_geral_percentual ?? 0
      }}
    />
  );
}
